import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Users, Building2, Sliders, Link2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { api, ApiRequestError } from '../lib/api';
import { friendlyError } from '../lib/error_codes';
import type { UserPublic } from '@nexaclaim/shared';

type Tab = 'organization' | 'users' | 'ai' | 'security' | 'integrations';

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: 'organization', label: 'Organization', icon: <Building2 className="h-4 w-4" /> },
  { id: 'users', label: 'Users', icon: <Users className="h-4 w-4" /> },
  { id: 'ai', label: 'AI Configuration', icon: <Sliders className="h-4 w-4" /> },
  { id: 'security', label: 'Security', icon: <ShieldCheck className="h-4 w-4" /> },
  { id: 'integrations', label: 'Integrations', icon: <Link2 className="h-4 w-4" /> },
];

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  rcm_director: 'RCM Director',
  auth_specialist: 'Auth Specialist',
  viewer: 'Viewer',
};

interface InviteModalProps {
  onClose: () => void;
  onInvite: (email: string, full_name: string, role: string) => void;
  isPending: boolean;
}

function InviteModal({ onClose, onInvite, isPending }: InviteModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState('auth_specialist');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-semibold text-gray-900">Invite User</h3>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="Jane Smith"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Work Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              placeholder="jane@organization.com"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-gray-600">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            >
              {Object.entries(ROLE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-sm text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isPending || !email || !name}
            onClick={() => onInvite(email, name, role)}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {isPending ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const qc = useQueryClient();
  const [showInvite, setShowInvite] = useState(false);

  const { data: users, isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.users.list(),
  });

  const inviteMutation = useMutation({
    mutationFn: ({ email, full_name, role }: { email: string; full_name: string; role: string }) =>
      api.users.invite({ email, full_name, role }),
    onSuccess: () => {
      toast.success('Invitation sent.');
      setShowInvite(false);
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId: string) => api.users.deactivate(userId),
    onSuccess: () => {
      toast.success('User deactivated.');
      qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  const resetMfaMutation = useMutation({
    mutationFn: (userId: string) => api.users.resetMfa(userId),
    onSuccess: () => {
      toast.success('MFA reset. User must re-enroll on next login.');
    },
    onError: (err) => {
      toast.error(friendlyError(err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR'));
    },
  });

  return (
    <div>
      {showInvite && (
        <InviteModal
          onClose={() => setShowInvite(false)}
          onInvite={(email, full_name, role) => inviteMutation.mutate({ email, full_name, role })}
          isPending={inviteMutation.isPending}
        />
      )}

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setShowInvite(true)}
          className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          Invite User
        </button>
      </div>

      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <div className="rounded-lg border border-gray-200">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-4 py-3 font-medium text-gray-600">Name</th>
                <th className="px-4 py-3 font-medium text-gray-600">Email</th>
                <th className="px-4 py-3 font-medium text-gray-600">Role</th>
                <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="px-4 py-3 font-medium text-gray-600">MFA</th>
                <th className="px-4 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(users ?? []).map((user: UserPublic) => (
                <tr key={user.id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{user.full_name}</td>
                  <td className="px-4 py-3 text-gray-600">{user.email}</td>
                  <td className="px-4 py-3 text-gray-600">
                    {ROLE_LABELS[user.role] ?? user.role}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                        user.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {user.mfa_enabled ? 'Enabled' : 'Disabled'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {user.mfa_enabled && (
                        <button
                          type="button"
                          onClick={() => resetMfaMutation.mutate(user.id)}
                          className="text-xs text-amber-600 hover:text-amber-800"
                        >
                          Reset MFA
                        </button>
                      )}
                      {user.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              window.confirm(
                                `Deactivate ${user.full_name}? They will lose access immediately.`,
                              )
                            ) {
                              deactivateMutation.mutate(user.id);
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Deactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OrganizationTab() {
  const { data: org, isLoading } = useQuery({
    queryKey: ['organization'],
    queryFn: () => api.organization.get(),
  });

  if (isLoading) return <LoadingSkeleton rows={4} />;
  if (!org) return null;

  return (
    <dl className="max-w-md space-y-4 text-sm">
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Organization Name</dt>
        <dd className="mt-1 font-medium text-gray-900">{org.name}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">NPI</dt>
        <dd className="mt-1 font-mono text-gray-900">{org.npi}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Subscription</dt>
        <dd className="mt-1 capitalize text-gray-900">{org.subscription_tier}</dd>
      </div>
      <div>
        <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Status</dt>
        <dd className="mt-1 capitalize text-gray-900">{org.status}</dd>
      </div>
    </dl>
  );
}

function AiTab() {
  return (
    <div className="max-w-md space-y-6">
      <p className="text-sm text-gray-500">
        AI configuration options are informational in v1. Changes require a support request.
      </p>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Recovery Threshold (informational)
        </label>
        <input
          type="range"
          min="0"
          max="100"
          defaultValue="70"
          disabled
          className="w-full cursor-not-allowed opacity-50"
        />
        <p className="mt-1 text-xs text-gray-400">
          Denials with recovery probability above this threshold are prioritized P1-P2.
        </p>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">Primary Model</label>
        <select
          disabled
          className="w-full cursor-not-allowed rounded border border-gray-200 px-3 py-2 text-sm text-gray-500 opacity-50"
        >
          <option>Claude Opus (default)</option>
        </select>
      </div>
    </div>
  );
}

function SecurityTab() {
  const { data: auditEntries, isLoading } = useQuery({
    queryKey: ['audit-system'],
    queryFn: () => api.audit.system({ page: 1, per_page: 5 }),
  });

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 p-4">
        <h3 className="mb-3 text-sm font-semibold text-gray-700">Session Policy</h3>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between">
            <dt className="text-gray-500">Session Timeout</dt>
            <dd className="font-medium text-gray-900">15 minutes (HIPAA)</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">MFA</dt>
            <dd className="font-medium text-gray-900">Required for all accounts</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-500">Password Policy</dt>
            <dd className="font-medium text-gray-900">12+ chars, rotated quarterly</dd>
          </div>
        </dl>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold text-gray-700">Recent System Events</h3>
        {isLoading ? (
          <LoadingSkeleton rows={3} />
        ) : !auditEntries || auditEntries.length === 0 ? (
          <p className="text-sm text-gray-400">No recent events.</p>
        ) : (
          <ol className="space-y-2">
            {auditEntries.map((entry) => (
              <li key={entry.id} className="flex gap-3 text-sm">
                <span className="text-xs text-gray-400 tabular-nums">
                  {new Date(entry.created_at).toLocaleString()}
                </span>
                <span className="text-gray-700">{entry.action}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

function IntegrationsTab() {
  return (
    <div className="max-w-md">
      <p className="text-sm text-gray-500">
        Clearinghouse and EHR integrations are available on Professional and Enterprise plans.
        Contact your account manager to configure.
      </p>
    </div>
  );
}

export function Settings() {
  const [activeTab, setActiveTab] = useState<Tab>('organization');

  const tabContent: Record<Tab, React.ReactNode> = {
    organization: <OrganizationTab />,
    users: <UsersTab />,
    ai: <AiTab />,
    security: <SecurityTab />,
    integrations: <IntegrationsTab />,
  };

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-gray-900">Settings</h1>

        <div className="flex gap-6">
          {/* Tab nav */}
          <nav className="w-44 flex-shrink-0 space-y-1">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium ${
                  activeTab === tab.id
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Tab content */}
          <div className="flex-1 rounded-lg border border-gray-200 bg-white p-6">
            {tabContent[activeTab]}
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}
